
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { clearStaffSessionStorage } from '../utils/staffSessionGuard';
import './Navbar.css';
import logoDark from '../Logos/priballblanca.png';
import logoLight from '../Logos/Priballl.png';
import { useEditMode } from '../contexts/EditModeContext';
import { canAccessAdminShell, hasStaffFeature, isFullAdminFromStorage } from '../utils/staffAccess';
import useGlobalTheme from '../hooks/useGlobalTheme';

const Navbar = ({ cart }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const fullAdmin = isFullAdminFromStorage();
  const canPanel = canAccessAdminShell();
  const canLocalSales = isAuthenticated && hasStaffFeature('localSales');
  const menuRef = useRef(null);
  const hamburgerRef = useRef(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const { isEditMode, toggleEditMode } = useEditMode();
  const { isDarkMode, toggleTheme } = useGlobalTheme();
  
  // Variables para el contador de clicks en el logo
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const clickTimeoutRef = useRef(null);

  const formatNumber = (number) => {
    if (typeof number !== 'number' || isNaN(number)) {
      return '0,00';
    }
    return number
      .toFixed(2) // Mantiene dos decimales
      .replace('.', ',') // Reemplaza el punto decimal por una coma
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // Agrega puntos como separadores de miles
  };

  useEffect(() => {
    const logoImage = document.querySelector('.logo img');
    if (logoImage) {
      logoImage.classList.add('loaded');
    }

    const handleClickOutside = (event) => {
      if (isOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !hamburgerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const calculateCartTotals = () => {
      const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const quantity = cart.reduce((sum, item) => sum + item.quantity, 0);

      let discountAmount = 0;
      cart.forEach(item => {
        if (item.discount) {
          discountAmount += (item.price * item.discount / 100) * item.quantity;
        }
      });

      const finalPrice = subtotal - discountAmount;
      setTotalPrice(finalPrice);
      setTotalQuantity(quantity);
    };

    calculateCartTotals();
  }, [cart]);

  // Función para manejar los clicks en el logo
  const handleLogoClick = (e) => {
    e.preventDefault();
    const currentTime = Date.now();
    
    // Si han pasado más de 2 segundos desde el último click, reiniciar contador
    if (currentTime - lastClickTime > 2000) {
      setLogoClickCount(1);
    } else {
      setLogoClickCount(prev => prev + 1);
    }
    
    setLastClickTime(currentTime);
    
    // Limpiar timeout anterior si existe
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    // Si es el primer click, navegar a home después de un breve delay
    if (logoClickCount === 0) {
      clickTimeoutRef.current = setTimeout(() => {
        navigate('/');
        setLogoClickCount(0);
      }, 300);
    }
    
    // Si son 3 clicks, navegar al login
    if (logoClickCount === 2) { // El tercer click (contando desde 0)
      clearTimeout(clickTimeoutRef.current);
      navigate('/login');
      setLogoClickCount(0);
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async () => {
    clearStaffSessionStorage();
    try {
      await signOut(auth);
    } catch (_) {
      /* ignore */
    }
    navigate('/login');
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <div className="logo">
          <img
            src={isDarkMode ? logoDark : logoLight}
            alt="Priball Logo"
            onClick={handleLogoClick}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <ul className={`nav-links ${isOpen ? 'open' : ''}`} ref={menuRef}>
          <li><Link to="/historia" onClick={toggleMenu}>Historia</Link></li>
          <li><Link to="/empresa" onClick={toggleMenu}>Empresa</Link></li>
          <li><Link to="/contacto" onClick={toggleMenu}>Contacto</Link></li>
          <li><Link to="/productos" onClick={toggleMenu}>Productos</Link></li>
          {/* Botón para Mayoristas */}
          <li><Link to="/lista-mayorista" onClick={toggleMenu}>Mayoristas</Link></li>
          <li>
            <Link to="/carrito" className="nav-cart-link" onClick={toggleMenu}>
              🛒 {totalQuantity > 0 && (
                <span className="cart-info">
                  ({totalQuantity}) ${formatNumber(totalPrice)}
                </span>
              )}
            </Link>
          </li>
          {canLocalSales && !canPanel && (
            <li>
              <NavLink
                to="/local"
                onClick={toggleMenu}
                className={({ isActive }) => (isActive ? 'nav-link-active' : undefined)}
                title="Volver a ventas en local"
              >
                Ventas en local
              </NavLink>
            </li>
          )}
          {isAuthenticated && canPanel && (
            <li><Link to="/admin" onClick={toggleMenu}>Panel</Link></li>
          )}
          {fullAdmin && (
            <li>
              <button 
                onClick={toggleEditMode}
                className={`edit-mode-btn ${isEditMode ? 'active' : ''}`}
                title={isEditMode ? 'Desactivar modo edición' : 'Activar modo edición'}
              >
                {isEditMode ? '✏️' : '📝'}
              </button>
            </li>
          )}
          {isAuthenticated && (
            <li>
              <button
                type="button"
                className="nav-logout-btn"
                onClick={handleLogout}
                aria-label="Cerrar sesión"
              >
                Salir
              </button>
            </li>
          )}
          <li>
            <button
              type="button"
              className="nav-theme-toggle"
              onClick={toggleTheme}
              aria-label={isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              title={isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
          </li>
        </ul>
        <div
          className={`hamburger ${isOpen ? 'open' : ''}`}
          onClick={toggleMenu}
          ref={hamburgerRef}
          role="button"
          aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={isOpen}
        >
          <span className="line"></span>
          <span className="line"></span>
          <span className="line"></span>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
